import {
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Link2Off,
  PlayCircle,
  UserX,
} from "lucide-react";
import type {
  InterviewFallbackVariant,
  InterviewFallbackConfig,
} from "./types";

export const INTERVIEW_FALLBACK_CANDIDATE_CONFIG: Record<
  InterviewFallbackVariant,
  InterviewFallbackConfig
> = {
  "too-early": {
    title: "You're a bit early",
    description:
      "This interview is not available yet. Please return closer to your scheduled interview time.",
    icon: Clock,
    iconColor: "text-brand-blue",
    iconBgColor: "bg-brand-light-blue-2/50",
    nextSteps: [
      "Check your invitation for the exact start time",
      "Ensure you're in a quiet place with good internet",
      "Refresh this page when it's time to start",
    ],
  },

  completed: {
    title: "Interview already completed",
    description: "Something went wrong, this interview is already completed",
    icon: CheckCircle,
    iconColor: "text-brand-green",
    iconBgColor: "bg-brand-light-green",
    nextSteps: [
      "Our AI is analyzing your responses and generating insights",
      "The hiring team will review your interview within 2–3 business days",
      "You'll receive an email with next steps",
    ],
  },

  expired: {
    title: "Interview link expired",
    description:
      "This interview session is no longer available. Please contact your recruiter if you believe this is a mistake.",
    icon: Link2Off,
    iconColor: "text-red-600",
    iconBgColor: "bg-red-50",
    nextSteps: [
      "Contact your recruiter for a new link",
      "Check if you have another invitation in your inbox",
    ],
  },

  invalid: {
    title: "Invalid interview link",
    description:
      "This link is invalid, expired, or already used. Please check your invitation email.",
    icon: AlertCircle,
    iconColor: "text-red-600",
    iconBgColor: "bg-red-50",
    nextSteps: [
      "Double-check the URL in your invitation email",
      "Make sure you haven't already completed this interview",
      "Reach out to support if the issue persists",
    ],
  },

  error: {
    title: "Unable to load interview",
    description:
      "Something went wrong while loading your interview. Please try again later.",
    icon: AlertTriangle,
    iconColor: "text-orange-600",
    iconBgColor: "bg-orange-50",
    nextSteps: [
      "Try refreshing the page",
      "Check your internet connection",
      "Contact support if the problem continues",
    ],
  },

  rejoin: {
    title: "Resume Interview",
    description:
      "You have an interview in progress. Click below to continue where you left off.",
    icon: PlayCircle,
    iconColor: "text-brand-blue",
    iconBgColor: "bg-brand-light-blue-2/50",
  },

  abandoned: {
    title: "Interview Abandoned",
    description:
      "You have left the interview session. This interview is marked as abandoned and can no longer be accessed.",
    icon: UserX,
    iconColor: "text-red-600",
    iconBgColor: "bg-red-50",
    nextSteps: [
      "Contact your recruiter if you believe this is a mistake",
      "Check your email for further instructions",
    ],
  },

  cancelled: {
    title: "Interview Cancelled",
    description:
      "This interview has been cancelled. Please contact your recruiter for more information or to reschedule.",
    icon: XCircle,
    iconColor: "text-red-600",
    iconBgColor: "bg-red-50",
    nextSteps: [
      "Check your email for any cancellation notices",
      "Reach out to your recruiter to reschedule",
      "Contact support if you have any questions",
    ],
  },
};

export const INTERVIEW_FALLBACK_PANEL_CONFIG: Partial<
  Record<InterviewFallbackVariant, InterviewFallbackConfig>
> = {
  completed: {
    title: "Interview Already Completed",
    description:
      "This interview session has concluded and was marked as completed.",
    icon: CheckCircle,
    iconColor: "text-brand-green",
    iconBgColor: "bg-brand-light-green",
    nextSteps: [
      "Candidate responses and evaluation data have been recorded",
      "You can review the detailed scorecard and AI report in the HR portal",
      "You may safely close this window",
    ],
  },

  expired: {
    title: "Interview Link Expired",
    description:
      "The link for this panel interview session has expired.",
    icon: Link2Off,
    iconColor: "text-red-600",
    iconBgColor: "bg-red-50",
    nextSteps: [
      "Check your HR dashboard for candidate status updates",
      "Contact the recruiting coordinator if a new session is needed",
      "You may safely close this window",
    ],
  },

  abandoned: {
    title: "Candidate Abandoned Session",
    description:
      "The candidate abandoned or left the interview session prematurely. This session is now closed.",
    icon: UserX,
    iconColor: "text-red-600",
    iconBgColor: "bg-red-50",
    nextSteps: [
      "The session has been recorded as candidate abandoned",
      "Check the candidate record on the HR portal for details",
      "You may safely close this window",
    ],
  },

  cancelled: {
    title: "Interview Cancelled",
    description:
      "This interview session has been cancelled.",
    icon: XCircle,
    iconColor: "text-red-600",
    iconBgColor: "bg-red-50",
    nextSteps: [
      "The interview was cancelled by the coordinator or candidate",
      "Check the HR portal dashboard for schedule updates",
      "You may safely close this window",
    ],
  },
};

export const INTERVIEW_FALLBACK_CONFIG = INTERVIEW_FALLBACK_CANDIDATE_CONFIG;

export const getFallbackConfig = (
  variant: InterviewFallbackVariant,
  role?: string | null
): InterviewFallbackConfig => {
  const isPanel = role === "panel";
  const configMap = isPanel
    ? INTERVIEW_FALLBACK_PANEL_CONFIG
    : INTERVIEW_FALLBACK_CANDIDATE_CONFIG;

  return (
    configMap[variant] ||
    INTERVIEW_FALLBACK_CANDIDATE_CONFIG[variant] ||
    INTERVIEW_FALLBACK_CANDIDATE_CONFIG.error
  );
};

